import { useCallback, useEffect, useMemo, useState } from "react";
import { AuthContext } from "./AuthContext";

const AUTH_STORAGE_KEY = "cf_auth_session";
const LEGACY_USER_KEY = "user";
const REFRESH_TOKEN_KEY = "refreshToken";

const parseStoredAuth = () => {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        token: parsed?.token || null,
        user: parsed?.user || null,
        expiresAt: parsed?.expiresAt || null,
      };
    }

    const legacyRaw = localStorage.getItem(LEGACY_USER_KEY);
    if (!legacyRaw) {
      return { token: null, user: null, expiresAt: null };
    }

    try {
      const legacyParsed = JSON.parse(legacyRaw);
      const legacyToken =
        (typeof legacyParsed === "string" ? legacyParsed : legacyParsed?.token) ||
        "";
      return {
        token: legacyToken || null,
        user: legacyParsed?.user || legacyParsed?.userProfile || null,
        expiresAt: null,
      };
    } catch {
      return { token: legacyRaw || null, user: null, expiresAt: null };
    }
  } catch (error) {
    console.error("Failed to parse auth session", error);
    localStorage.removeItem(AUTH_STORAGE_KEY);
    return { token: null, user: null, expiresAt: null };
  }
};

export const AuthProvider = ({ children }) => {
  const initial = parseStoredAuth();
  const [token, setTokenState] = useState(initial.token);
  const [user, setUser] = useState(initial.user);

  const persistAuth = useCallback((nextToken, nextUser, expiresAt) => {
    if (!nextToken) {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      return;
    }

    localStorage.setItem(
      AUTH_STORAGE_KEY,
      JSON.stringify({
        token: nextToken,
        user: nextUser || null,
        expiresAt: expiresAt || null,
      }),
    );
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    localStorage.removeItem(LEGACY_USER_KEY);
    localStorage.removeItem("otpUserId");
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    setTokenState(null);
    setUser(null);
  }, []);

  const setToken = useCallback(
    (nextToken) => {
      setTokenState(nextToken || null);
      persistAuth(nextToken || null, user, null);
    },
    [persistAuth, user],
  );

  const login = useCallback(
    (newToken, userData, expiresIn) => {
      if (!newToken) {
        return;
      }
      const expiresAt = expiresIn ? Date.now() + expiresIn * 1000 : null;
      setTokenState(newToken);
      setUser(userData || null);
      persistAuth(newToken, userData || null, expiresAt);
      // NOTE: the JWT-tied refresh + idle modal logic now lives in
      // SessionProvider. AuthProvider no longer schedules its own
      // hard-logout-at-expiry timer; that was conflicting with the new
      // silent-refresh behavior.
    },
    [persistAuth],
  );

  const loginFromResponse = useCallback(
    (responseData) => {
      const responseToken = responseData?.token || responseData?.accessToken;
      const responseUser =
        responseData?.userProfile || responseData?.user || responseData?.data;
      const expiresIn = responseData?.expiresIn;

      if (!responseToken) {
        return;
      }

      login(responseToken, responseUser || null, expiresIn);

      // Store the refresh token alongside the access token so the
      // JWT-tied refresh flow in SessionProvider can use it when the
      // access token expires. The backend returns this on every login
      // response (alongside `token` and `userProfile`); without
      // persisting it here, performRefresh would throw "No refresh
      // token available" and the user would be force-logged-out the
      // first time the refresh modal popped.
      //
      // Both possible backend field names are tried: `refreshToken`
      // (most common) and `refresh` (some backends use the shorter
      // form). The same dedup applies to the SessionProvider's
      // refresh response handler.
      const responseRefreshToken =
        responseData?.refreshToken ||
        responseData?.refresh_token ||
        responseData?.refresh;
      if (responseRefreshToken) {
        try {
          localStorage.setItem(REFRESH_TOKEN_KEY, responseRefreshToken);
        } catch {
          // localStorage might be disabled in some privacy modes;
          // silently fail — the session will still work until the
          // first refresh attempt, at which point the user will be
          // logged out. Acceptable trade-off.
        }
      }
    },
    [login],
  );

  const getToken = useCallback(() => token, [token]);

  // Backwards-compat no-op. SessionProvider owns session expiry handling.
  useEffect(() => {
    return undefined;
  }, []);

  const { isStaffLogin, isClientLogin } = useMemo(() => {
    const userType = (user?.userType || user?.UserType || "").toLowerCase();
    const staffRoles = ["superuser", "internaluser", "staff", "admin"];
    const isStaff = staffRoles.includes(userType);
    const isClient = !isStaff && userType === "client";
    return { isStaffLogin: isStaff, isClientLogin: isClient };
  }, [user]);

  const value = useMemo(
    () => ({
      token,
      user,
      login,
      loginFromResponse,
      setToken,
      getToken,
      logout,
      isAuthenticated: Boolean(token),
      isStaffLogin,
      isClientLogin,
    }),
    [
      token,
      user,
      login,
      loginFromResponse,
      setToken,
      getToken,
      logout,
      isStaffLogin,
      isClientLogin,
    ],
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
};


